import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from "@vaiu/ui/card";
import { Loader2 } from 'lucide-react';
import HomePage from '@/pages/HomePage';
import { analyticsService } from '@/services/analyticsService';
import { platformApi } from '@/services/platformApi';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Optional roles required to access this route */
  requiredRoles?: string[];
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Allow guest users to access this route */
  allowGuests?: boolean;
  /** Allow unauthenticated access without auto-logging in as guest */
  allowUnauthenticated?: boolean;
}

/**
 * ProtectedRoute - Authentication wrapper for all routes
 * 
 * PUBLIC ROUTES (allowGuests=true):
 * - Automatically creates guest session for visitors
 * - No login required - seamless access with guest userId
 * - Guest can perform actions like any authenticated user
 * - This is our definition of "public" routes
 * 
 * PROTECTED ROUTES (default):
 * - Requires authentication via login
 * - Redirects to login page with return URL
 * - Optional role-based access control
 * 
 * Current Public Routes (visitors auto-login as guests):
 * - /pay - Payment page for sending money to merchants
 * - /merchant/:id - Public merchant profile pages
 * - /storefront/:id - Merchant storefront (legacy)
 * - /invoice/:code - Invoice payment page
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  loadingComponent,
  allowGuests = false,
  allowUnauthenticated = false
}) => {
  const { user, loading, isGuest, login } = useAuth();
  const location = useLocation();
  const [autoGuestLoginAttempted, setAutoGuestLoginAttempted] = useState(false);
  const [autoGuestLoginInProgress, setAutoGuestLoginInProgress] = useState(false);
  
  // Store the initial location to prevent navigation during auto-guest-login
  const initialLocationRef = useRef(location);

  // PUBLIC ROUTE BEHAVIOR: Auto-guest-login for routes that allow guests
  // This is how we define public routes - visitors automatically become guests with a userId
  useEffect(() => {
    const performAutoGuestLogin = async () => {
      // For PUBLIC routes (allowGuests=true), automatically create guest session
      // This gives visitors a real userId without requiring any login
      if (allowGuests && !user && !loading && !autoGuestLoginAttempted && !autoGuestLoginInProgress && !allowUnauthenticated) {
        console.log('🌐 PUBLIC ROUTE: Auto-creating guest session for', location.pathname, 'with search:', location.search);
        setAutoGuestLoginInProgress(true);
        setAutoGuestLoginAttempted(true);
        
        try {
          // Create guest session
          const result = await platformApi.guestLogin();
          
          if (result && result.token) {
            console.log('✅ PUBLIC ROUTE: Guest userId created:', result.userId);
            
            // Log the visitor in as a guest with a real userId
            await login(result.token, true, {
              username: result.username,
              userId: result.userId,
              sessionExpiry: result.sessionExpiry
            });
            
            console.log('✅ PUBLIC ROUTE: Guest session active, userId:', result.userId, 'on route:', location.pathname);
            
            // Track analytics if available
            if (typeof analyticsService !== 'undefined' && analyticsService.trackGuestLogin) {
              try {
                analyticsService.trackGuestLogin(result.username, result.sessionExpiry);
              } catch (analyticsError) {
                console.warn('Analytics tracking failed:', analyticsError);
              }
            }
          }
        } catch (error) {
          console.error('❌ Auto-guest-login failed:', error);
          // Don't throw - just let it fall through to redirect to login
        } finally {
          setAutoGuestLoginInProgress(false);
        }
      }
    };

    performAutoGuestLogin();
  }, [allowGuests, user, loading, autoGuestLoginAttempted, autoGuestLoginInProgress, login, location.pathname, allowUnauthenticated]);

  // Show loading state while authentication is being determined or auto-guest-login is in progress
  if (loading || (allowGuests && !user && autoGuestLoginInProgress)) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600">
                {autoGuestLoginInProgress ? 'Creating guest session...' : 'Verifying authentication...'}
              </p>
              {autoGuestLoginInProgress && (
                <p className="text-xs text-gray-500 mt-2">
                  You'll be able to use all features as a guest user
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is not authenticated - redirect to login with return URL
  // UNLESS we're in the process of auto-guest-login
  if (!user && !(allowGuests && autoGuestLoginInProgress) && !allowUnauthenticated) {
    // Construct the return URL with current path and search params
    const returnUrl = `${location.pathname}${location.search}`;
    
    // Check if this is a redeem route - if so, add forRedeem parameter to hide guest option
    const isRedeemRoute = location.pathname.startsWith('/redeem');
    const loginUrl = isRedeemRoute
      ? `/login?forRedeem=true&redirect=${encodeURIComponent(returnUrl)}`
      : `/login?redirect=${encodeURIComponent(returnUrl)}`;
    
    console.log('🔐 User not authenticated, redirecting to login with return URL:', returnUrl, isRedeemRoute ? '(redeem route)' : '');
    
    return <Navigate to={loginUrl} replace />;
  }
  
  // If we're still waiting for auto-guest-login and there's no user, don't render anything yet
  if (!user && allowGuests && autoGuestLoginInProgress) {
    return null; // Will show loading state from the condition above
  }

  // Check if user is a guest and route doesn't allow guests
  if (isGuest && !allowGuests) {
    const allowedGuestPaths = [
      '/',            // Allow root (dashboard) for guests
      '/dashboard',   // Backward compatibility (redirects to /)
      '/pay',         // Allow payment features
      '/profile',     // Allow viewing profile
      '/balances',    // Allow viewing balances
      '/transfer',    // Allow creating tokens
      '/transactions' // Allow viewing transaction history
    ];
    const currentPath = location.pathname.toLowerCase();
    
    // Check if current path is allowed for guests
    const isAllowedPath = allowedGuestPaths.some(path => 
      currentPath.startsWith(path.toLowerCase())
    );
    
    if (!isAllowedPath) {
      console.log('👤 Guest user attempting to access restricted route:', location.pathname);
      
      // Track guest restriction in analytics
      analyticsService.trackGuestRestriction(location.pathname);
      
      // Show HomePage content while keeping guest logged in
      return <HomePage isGuestRestricted={true} attemptedPath={location.pathname} />;
    }
  }

  // Check role-based access if required roles are specified
  if (requiredRoles.length > 0) {
    const userRoles = user.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    
    if (!hasRequiredRole) {
      console.warn('🚫 User lacks required roles for route:', {
        userRoles,
        requiredRoles,
        user: user.username || user.emails?.[0]?.address
      });
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
                <p className="text-sm text-gray-600 mb-6">
                  You don't have permission to access this page. Required roles: {requiredRoles.join(', ')}
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Go Back
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // User is authenticated and has required permissions - render protected content
  console.log('🔓 ProtectedRoute: Rendering children for', location.pathname, 'user:', user?.username || user?.emails?.[0]?.address, 'isGuest:', isGuest);
  return <>{children}</>;
};

export default ProtectedRoute; 